import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  Skeleton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import EmailCampaignCalendar from "../components/EmailCampaignCalendar";
import BlacklistReviewPanel from "../components/BlacklistReviewPanel";
import BroadcastBudgetPanel from "../components/BroadcastBudgetPanel";
import { emailDashboardApi } from "../services/emailDashboardApi";
import { shouldPollCampaigns } from "../utils/campaignPolling";
import api from "../axiosConfig";

// Harare = UTC+2, no DST, fixed forever. All helpers use explicit arithmetic — zero browser-timezone dependency.
const HARARE_OFFSET_MS = 2 * 60 * 60 * 1000;
const HARARE_OFFSET_STR = "+02:00";
const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Normalize bare UTC API strings (e.g. "2026-05-26 02:05:00" — no Z suffix) → add "Z".
function toUtcIso(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return (s.includes(" ") ? s.replace(" ", "T") : s) + "Z";
}

// UTC Date → "YYYY-MM-DDTHH:mm" in Harare time (for <input type="datetime-local">).
function toHarareDatetimeValue(utcDate) {
  const h = new Date(utcDate.getTime() + HARARE_OFFSET_MS);
  return `${h.getUTCFullYear()}-${String(h.getUTCMonth()+1).padStart(2,"0")}-${String(h.getUTCDate()).padStart(2,"0")}T${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
}

// "YYYY-MM-DDTHH:mm" treated as Harare time → UTC Date (browser timezone irrelevant).
function parseHarareDatetimeInput(str) {
  if (!str) return null;
  const d = new Date(str.slice(0, 16) + ":00" + HARARE_OFFSET_STR);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "YYYY-MM-DDTHH:mm" Harare string → "YYYY-MM-DDTHH:mm:00+02:00" for API.
function toHarareOffsetIso(localStr) {
  if (!localStr) return null;
  return localStr.slice(0, 16) + ":00" + HARARE_OFFSET_STR;
}

// Format a UTC API datetime (bare or with Z) → "DD Mon YYYY, HH:mm" in Harare.
function fmtHarare(raw) {
  if (!raw) return "—";
  try {
    const dt = new Date(toUtcIso(raw));
    if (Number.isNaN(dt.getTime())) return "—";
    const h = new Date(dt.getTime() + HARARE_OFFSET_MS);
    return `${String(h.getUTCDate()).padStart(2,"0")} ${_MONTHS[h.getUTCMonth()]} ${h.getUTCFullYear()}, ${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
  } catch { return "—"; }
}


// ─── Campaign Detail / Edit Dialog ───────────────────────────────────────────
export function CampaignDetailDialog({ campaign: initial, open, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsMeta, setRecipientsMeta] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [rescheduleValue, setRescheduleValue] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snack, setSnack] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [ctaClicks, setCtaClicks] = useState(null);

  const id = initial?.id;

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await api.get(`/email/campaigns/${id}`);
      setDetail(res.data);
      if (res.data?.schedule_at) {
        setRescheduleValue(toHarareDatetimeValue(new Date(toUtcIso(res.data.schedule_at))));
      } else {
        setRescheduleValue(toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000)));
      }
    } catch (e) {
      setSnack(e?.response?.data?.message || "Failed to load campaign.");
    } finally {
      setDetailLoading(false);
    }
  }, [id]);

  const loadRecipients = useCallback(async (page = 1) => {
    if (!id) return;
    setRecipientsLoading(true);
    try {
      const res = await api.get(`/email/campaigns/${id}/recipients`, { params: { per_page: 20, page } });
      setRecipients(res.data?.recipients?.data ?? []);
      setRecipientsMeta(res.data?.recipients ?? null);
    } catch {
      // non-critical
    } finally {
      setRecipientsLoading(false);
    }
  }, [id]);

  const loadCtaClicks = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/email/campaigns/${id}/cta-clicks`);
      setCtaClicks(res.data);
    } catch {
      // non-critical
    }
  }, [id]);

  useEffect(() => {
    if (open && id) {
      setDetail(null);
      setRecipients([]);
      setRecipientsPage(1);
      setCtaClicks(null);
      setSnack("");
      loadDetail();
      loadRecipients(1);
      loadCtaClicks();
    }
  }, [open, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReschedule = async () => {
    const dt = parseHarareDatetimeInput(rescheduleValue);
    if (!dt || dt.getTime() <= Date.now() + 2 * 60 * 1000) {
      setSnack("Choose a time at least 2 minutes in the future.");
      return;
    }
    setRescheduleLoading(true);
    try {
      await emailDashboardApi.updateSchedule(id, {
        schedule_at: toHarareOffsetIso(rescheduleValue),
        tz: "Africa/Harare",
      });
      setSnack("Schedule updated.");
      await loadDetail();
      onChanged?.();
    } catch (e) {
      setSnack(e?.response?.data?.message || "Failed to update schedule.");
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      // A 'sending' (often paced/stuck) campaign needs force=1 to cancel the
      // remaining sends and delete it; other statuses ignore the flag.
      const params = isSending ? { force: 1 } : {};
      await api.delete(`/email/campaigns/${id}`, { params });
      setDeleteConfirmOpen(false);
      onChanged?.();
      onClose();
    } catch (e) {
      setSnack(e?.response?.data?.message || "Failed to delete campaign.");
      setDeleteConfirmOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRemoveRecipient = async (recipientId) => {
    setRemovingId(recipientId);
    try {
      await api.delete(`/email/campaigns/${id}/recipients/${recipientId}`);
      setRecipients((prev) => prev.filter((r) => r.id !== recipientId));
      setSnack("Recipient removed.");
      await loadDetail();
      onChanged?.();
    } catch (e) {
      setSnack(e?.response?.data?.message || "Failed to remove recipient.");
    } finally {
      setRemovingId(null);
    }
  };

  const handleRecipientsPageChange = (_, page) => {
    setRecipientsPage(page);
    loadRecipients(page);
  };

  const canReschedule = detail && !["sending", "sent", "completed", "failed"].includes(String(detail.status || "").toLowerCase());
  const isSending = String(detail?.status || "").toLowerCase() === "sending";
  // Finished campaigns keep their history; everything else (including a stuck/paced
  // 'sending' campaign) can be deleted — 'sending' is force-cancelled on the server.
  const canDelete = detail && !["sent", "completed"].includes(String(detail.status || "").toLowerCase());
  const actualTotal = detail?.counts?.total ?? 0;
  const expectedTotal = detail?.totals_json?.expected ?? 0;
  const showExpected =
    actualTotal === 0 &&
    ["filter_all", "page", "raw_list"].includes(detail?.scope) &&
    detail?.status === "scheduled" &&
    expectedTotal > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, pr: 6 }}>
          {detailLoading ? "Loading…" : (detail?.subject || initial?.title || `Campaign #${id}`)}
          <IconButton size="small" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {snack ? (
            <Alert severity="info" onClose={() => setSnack("")} sx={{ mb: 2 }}>
              {snack}
            </Alert>
          ) : null}

          {detailLoading ? (
            <Stack spacing={1}>
              <Skeleton height={28} />
              <Skeleton height={20} width="60%" />
              <Skeleton height={20} width="45%" />
            </Stack>
          ) : detail ? (
            <Stack spacing={2.5}>
              {/* ── Info ── */}
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Chip size="small" label={detail.status} color={statusColor(detail.status)} />
                  {detail.scope ? <Chip size="small" label={detail.scope} variant="outlined" /> : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  <strong>Scheduled:</strong> {fmtHarare(detail.schedule_at)}
                </Typography>
                {detail.created_by ? (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Created by:</strong> {detail.created_by}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  <strong>Recipients:</strong>{" "}
                  {showExpected
                    ? `~${expectedTotal.toLocaleString()} expected (pending dispatch)`
                    : actualTotal.toLocaleString()}
                  {actualTotal > 0
                    ? ` · Sent ${detail.counts.sent} · Failed ${detail.counts.failed} · Queued ${detail.counts.queued}`
                    : ""}
                </Typography>
                {ctaClicks && ctaClicks.total_clicks > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Interactions:</strong>{" "}
                    {ctaClicks.total_clicks} interaction{ctaClicks.total_clicks !== 1 ? "s" : ""} from {ctaClicks.unique_clickers} contact{ctaClicks.unique_clickers !== 1 ? "s" : ""}
                  </Typography>
                ) : null}
              </Box>

              {/* ── Reschedule ── */}
              {canReschedule ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                    <EditCalendarIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                    Change schedule
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="datetime-local"
                      size="small"
                      value={rescheduleValue}
                      onChange={(e) => setRescheduleValue(e.target.value)}
                      inputProps={{ min: toHarareDatetimeValue(new Date(Date.now() + 2 * 60 * 1000)) }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleReschedule}
                      disabled={rescheduleLoading}
                      sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                    >
                      {rescheduleLoading ? <CircularProgress size={16} /> : "Save"}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {rescheduleValue && parseHarareDatetimeInput(rescheduleValue)
                      ? `→ ${fmtHarare(toHarareOffsetIso(rescheduleValue))} (Harare time)`
                      : ""}
                  </Typography>
                </Box>
              ) : null}

              {/* ── Pending dispatch notice (filter_all scheduled, no rows yet) ── */}
              {showExpected && recipients.length === 0 ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                    Recipients (~{expectedTotal.toLocaleString()} expected)
                  </Typography>
                  <Alert severity="info" icon={false} sx={{ fontSize: 12, py: 0.5 }}>
                    Individual recipient rows are created automatically when the campaign dispatches on{" "}
                    <strong>{fmtHarare(detail.schedule_at)}</strong>. They will appear here after that time.
                  </Alert>
                </Box>
              ) : null}

              {/* ── Recipients list ── */}
              {actualTotal > 0 || recipients.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                    Recipients ({actualTotal.toLocaleString()})
                  </Typography>
                  {recipientsLoading ? (
                    <Stack spacing={0.5}>
                      {[1, 2, 3].map((i) => <Skeleton key={i} height={40} />)}
                    </Stack>
                  ) : (
                    <>
                      <List dense disablePadding>
                        {recipients.map((r) => {
                          const canRemove = !["sent", "delivered", "opened"].includes(String(r.status || "").toLowerCase());
                          return (
                            <ListItem key={r.id} divider sx={{ px: 0 }}>
                              <ListItemText
                                primary={r.email_snapshot || r.email}
                                secondary={
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip size="small" label={r.status || "unknown"} color={statusColor(r.status)} sx={{ height: 18, fontSize: 10 }} />
                                    {r.name_snapshot || r.name ? (
                                      <Typography variant="caption" color="text.secondary">{r.name_snapshot || r.name}</Typography>
                                    ) : null}
                                  </Stack>
                                }
                              />
                              <ListItemSecondaryAction>
                                {canRemove ? (
                                  <Tooltip title="Remove from campaign">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveRecipient(r.id)}
                                        disabled={removingId === r.id}
                                      >
                                        {removingId === r.id ? <CircularProgress size={14} /> : <PersonRemoveIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : null}
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                      {recipientsMeta?.last_page > 1 ? (
                        <Pagination
                          count={recipientsMeta.last_page}
                          page={recipientsPage}
                          onChange={handleRecipientsPageChange}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      ) : null}
                    </>
                  )}
                </Box>
              ) : null}
              {/* ── CTA Who Clicked ── */}
              {ctaClicks && ctaClicks.unique_clickers > 0 ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                    🖱️ Interactions ({ctaClicks.unique_clickers} contact{ctaClicks.unique_clickers !== 1 ? "s" : ""}, {ctaClicks.total_clicks} total)
                  </Typography>
                  <List dense disablePadding>
                    {ctaClicks.clickers.map((c) => (
                      <ListItem key={c.id} divider sx={{ px: 0 }}>
                        <ListItemText
                          primary={c.email}
                          secondary={
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {c.name ? (
                                <Typography variant="caption" color="text.secondary">{c.name} ·</Typography>
                              ) : null}
                              <Typography variant="caption" color="text.secondary">
                                {c.click_count} interaction{c.click_count !== 1 ? "s" : ""}
                              </Typography>
                              {c.first_clicked_at ? (
                                <Typography variant="caption" color="text.secondary">
                                  · {fmtHarare(c.first_clicked_at)}
                                </Typography>
                              ) : null}
                            </Stack>
                          }
                        />
                        <Chip
                          size="small"
                          label={c.click_count}
                          color="success"
                          sx={{ height: 20, fontWeight: 900 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
          {canDelete ? (
            <Button
              startIcon={<DeleteOutlineIcon />}
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              {isSending ? "Stop & delete" : "Delete campaign"}
            </Button>
          ) : <Box />}
          <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 800 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{isSending ? "Stop & delete campaign?" : "Delete campaign?"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isSending
              ? "This campaign is still sending. Deleting it stops any remaining/queued sends and permanently removes the campaign and its recipient records. Emails already sent cannot be recalled. This cannot be undone."
              : "This will permanently delete the campaign and all its recipient records. This cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {deleteLoading ? <CircularProgress size={16} /> : "Yes, delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// --------------------- helpers
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtNumber(n) {
  return new Intl.NumberFormat().format(safeNum(n));
}

function fmtWhen(iso) {
  return fmtHarare(iso);
}

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "scheduled") return "warning";
  if (s === "sending" || s === "queued") return "info";
  if (s === "sent" || s === "delivered") return "success";
  if (s === "failed" || s === "bounced") return "error";
  return "default";
}

// --------------------- UI blocks (WhatsApp-style)
function KpiMiniCard({ icon, title, value, caption }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 999,
            border: "1px solid #E9ECEF",
            display: "grid",
            placeItems: "center",
            bgcolor: "white",
            fontSize: 18,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {title}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {caption ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function KpiMiniCardSkeleton({ title }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Skeleton variant="circular" width={42} height={42} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {title}
          </Typography>
          <Skeleton width="45%" height={28} />
          <Skeleton width="70%" height={18} />
        </Box>
      </Stack>
    </Paper>
  );
}

function DeliveryFunnelCard({ sent, delivered, opened, failed }) {
  const total = Math.max(1, sent + delivered + opened + failed);

  const segs = [
    { label: "Sent", value: sent, dot: "#3B82F6" },
    { label: "Delivered", value: delivered, dot: "#22C55E" },
    { label: "Opened", value: opened, dot: "#64748B" },
    { label: "Failed", value: failed, dot: "#EF4444" },
  ];

  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        Delivery Funnel
      </Typography>

      <Box
        sx={{
          display: "flex",
          overflow: "hidden",
          borderRadius: 999,
          border: "1px solid #E9ECEF",
          height: 34,
          bgcolor: "#F8FAFC",
        }}
      >
        {segs.map((s) => {
          const w = (s.value / total) * 100;
          return (
            <Box
              key={s.label}
              sx={{
                width: `${w}%`,
                minWidth: s.value > 0 ? 14 : 0,
                bgcolor: s.dot,
                opacity: 0.25,
              }}
              title={`${s.label}: ${s.value}`}
            />
          );
        })}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
        {segs.map((s) => (
          <Stack key={s.label} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: s.dot }} />
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {s.label}
            </Typography>
            <Chip size="small" label={fmtNumber(s.value)} sx={{ height: 20 }} />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function DeliveryFunnelCardSkeleton() {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        Delivery Funnel
      </Typography>
      <Skeleton variant="rectangular" height={34} sx={{ borderRadius: 999, mb: 1 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
        {[1, 2, 3, 4].map((i) => (
          <Stack key={i} direction="row" spacing={0.75} alignItems="center">
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton width={48} height={18} />
            <Skeleton width={34} height={20} sx={{ borderRadius: 999 }} />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function TotalContactsCard({ total, caption }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 999,
            border: "1px solid #E9ECEF",
            display: "grid",
            placeItems: "center",
            bgcolor: "white",
            fontSize: 18,
          }}
        >
          👥
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, opacity: 0.8 }}>
            Total Contacts
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            {fmtNumber(total)}
          </Typography>
          {caption ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function TotalContactsCardSkeleton() {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Skeleton variant="circular" width={44} height={44} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="45%" height={18} />
          <Skeleton width="60%" height={42} />
          <Skeleton width="70%" height={16} />
        </Box>
      </Stack>
    </Paper>
  );
}

function ContactedRingCard({ contacted, availableReach, blocked }) {
  const total = Math.max(1, contacted + availableReach + blocked);
  const pct = Math.round((contacted / total) * 100);

  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Box sx={{ position: "relative", width: 120, height: 120 }}>
          <CircularProgress variant="determinate" value={pct} size={120} thickness={5} sx={{ color: "#22C55E" }} />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1 }}>
              {fmtNumber(contacted)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Contacted
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack spacing={1}>
            {[
              { label: "Contacted", v: contacted, c: "#22C55E" },
              { label: "Available Reach", v: availableReach, c: "#94A3B8" },
              { label: "Blocked", v: blocked, c: "#EF4444" },
            ].map((row) => (
              <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: row.c }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {fmtNumber(row.v)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function ContactedRingCardSkeleton() {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Skeleton variant="circular" width={120} height={120} />
        <Box sx={{ flex: 1 }}>
          <Stack spacing={1.2}>
            {[1, 2, 3].map((i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={10} height={10} />
                  <Skeleton width={120} height={18} />
                </Stack>
                <Skeleton width={50} height={18} />
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function CampaignsList({ calendarItems = [], onSelectCampaign }) {
  const rows = useMemo(() => {
    const copy = [...(calendarItems || [])];
    copy.sort((a, b) =>
      String(b.sent_at || b.schedule_at || "").localeCompare(String(a.sent_at || a.schedule_at || ""))
    );
    return copy;
  }, [calendarItems]);

  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        Campaigns
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          No campaigns yet.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1 }}>
          {rows.map((c) => (
            <Paper
              key={c.id}
              variant="outlined"
              onClick={() => onSelectCampaign?.(c)}
              sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", "&:hover": { boxShadow: 2, borderColor: "#10B981" } }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 900 }} noWrap>
                    {c.title || c.name || `Campaign #${c.id}`}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                    <Chip size="small" label={c.status || "unknown"} color={statusColor(c.status)} />
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                      {c.schedule_at
                        ? `Scheduled: ${fmtWhen(c.schedule_at)}`
                        : c.sent_at
                        ? `Sent: ${fmtWhen(c.sent_at)}`
                        : `Created: ${fmtWhen(c.created_at)}`}
                    </Typography>
                  </Stack>

                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.75 }}>
                    {safeNum(c?.counts?.total) > 0
                      ? `Sent: ${safeNum(c?.counts?.sent)} • Failed: ${safeNum(c?.counts?.failed)} • Queued: ${safeNum(c?.counts?.queued)}`
                      : ["filter_all", "page", "raw_list"].includes(c?.scope) && c?.status === "scheduled" && safeNum(c?.totals_json?.expected) > 0
                      ? `~${safeNum(c?.totals_json?.expected).toLocaleString()} recipients (pending dispatch)`
                      : "No recipients yet"}
                  </Typography>
                </Box>

                {typeof c?.progress === "number" ? (
                  <Box sx={{ width: 160 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Progress
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, c.progress))}
                      sx={{ borderRadius: 999 }}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );
}

function CampaignsListSkeleton() {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        Campaigns
      </Typography>

      <Box sx={{ display: "grid", gap: 1 }}>
        {[1, 2, 3].map((i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Skeleton width="45%" height={22} />
            <Skeleton width="75%" height={18} />
            <Skeleton width="55%" height={18} />
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}

// --------------------- main
export default function EmailBroadcastsDashboard({ embedded = false, onNewCampaign }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(0); // 0=Campaigns, 1=Calendar
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [blacklistStats, setBlacklistStats] = useState({ total_blacklisted: 0, blacklisted_last_7_days: 0 });
  const [blacklistOpen, setBlacklistOpen] = useState(false);

  const [emailBudget, setEmailBudget] = useState(null);
  const [emailBudgetLoading, setEmailBudgetLoading] = useState(true);

  // silent=true is used by the background poll: no spinner/skeleton flicker and
  // a transient failure is swallowed so the last-good data stays on screen.
  const load = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setErr("");
    }
    try {
      const d = await emailDashboardApi.getDashboard(days);
      setData(d);
    } catch (e) {
      if (!silent) setErr(e?.response?.data?.message || e?.message || "Failed to load email dashboard.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadEmailBudget = useCallback(async () => {
    setEmailBudgetLoading(true);
    try {
      const res = await api.get("/analytics/marketing/broadcast-budget");
      const channels = res.data?.data?.channels || res.data?.channels || {};
      setEmailBudget(channels.email || null);
    } catch {
      // non-critical
    } finally {
      setEmailBudgetLoading(false);
    }
  }, []);

  const loadBlacklistStats = async () => {
    try {
      const d = await emailDashboardApi.getBlacklistStats();
      setBlacklistStats({
        total_blacklisted: Number(d?.total_blacklisted ?? 0),
        blacklisted_last_7_days: Number(d?.blacklisted_last_7_days ?? 0),
      });
    } catch {
      // non-critical — silently ignore
    }
  };

  useEffect(() => {
    load();
    loadBlacklistStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => {
    loadEmailBudget();
  }, [loadEmailBudget]);

  // A just-sent campaign lands as scheduled/queued and only flips to "sent" once
  // the worker processes it. We auto-refresh so the user never has to hit Refresh.
  //
  // The "is anything in flight?" decision depends on the CURRENT time (a campaign
  // scheduled while this page is open becomes due later), so it MUST be evaluated
  // on each tick with a fresh clock — never memoised on the data snapshot, or the
  // interval would never start for a campaign that wasn't already due at load.
  // Hence: one steady interval kept in a ref, re-checking live each tick.
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return; // pause in background tabs
      if (shouldPollCampaigns(dataRef.current?.calendar)) {
        load(true);
      }
    }, 15000);
    return () => clearInterval(id);
    // load only closes over `days`; re-arm the timer when the range changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const cards = data?.cards || {};
  const funnel = data?.funnel || {};
  const audience = data?.audience || {};
  const calendar = data?.calendar || [];

  const sent = safeNum(funnel.sent);
  const delivered = safeNum(funnel.delivered);
  const opened = safeNum(funnel.opened);
  const failed = safeNum(funnel.failed);

  const contacted = sent;
  const availableReach = safeNum(audience.deliverable_active);
  const blocked = safeNum(audience.inactive);

  const lastRunLabel = cards?.last_campaign_run?.label ?? "—";
  const lastRunWhen = cards?.last_campaign_run?.when ?? null;

  const scheduledCount =
    safeNum(cards?.active_campaigns?.scheduled) || safeNum(cards?.active_campaigns?.active) || 0;

  const throughput = safeNum(cards?.throughput?.value) || safeNum(cards?.msgs_per_hour?.value) || 0;

  const totalContacts = safeNum(cards?.total_contacts?.total);
  const totalContactsCaption = cards?.total_contacts?.caption ?? "";

  const showSkeleton = loading && !data;

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {loading ? (
        <Box sx={{ position: "sticky", top: 0, zIndex: 5, mb: 1 }}>
          <LinearProgress />
        </Box>
      ) : null}

      {/* Header / Tools row */}
      {embedded ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          {/* ✅ Fix 2: New Campaign button appears in embedded mode */}
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={() => onNewCampaign?.()}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
          >
            New Campaign
          </Button>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Range</InputLabel>
            <Select
              label="Range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      ) : (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Email Broadcasts
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* ✅ Also show in non-embedded mode */}
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => onNewCampaign?.()}
              sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
            >
              New Campaign
            </Button>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Range</InputLabel>
              <Select
                label="Range"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      )}

      {!loading && err ? <Alert severity="error">{err}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.9fr 1fr" },
          mb: 2,
        }}
      >
        {showSkeleton ? (
          <>
            <DeliveryFunnelCardSkeleton />
            <TotalContactsCardSkeleton />
            <ContactedRingCardSkeleton />
          </>
        ) : (
          <>
            <DeliveryFunnelCard sent={sent} delivered={delivered} opened={opened} failed={failed} />
            <TotalContactsCard total={totalContacts} caption={totalContactsCaption} />
            <ContactedRingCard contacted={contacted} availableReach={availableReach} blocked={blocked} />
          </>
        )}
      </Box>

      <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #E9ECEF", mb: 2 }} elevation={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
          Activity Overview
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          }}
        >
          {showSkeleton ? (
            <>
              <KpiMiniCardSkeleton title="Last Campaign Run" />
              <KpiMiniCardSkeleton title="Scheduled Campaigns" />
              <KpiMiniCardSkeleton title="msgs/hr" />
              <KpiMiniCardSkeleton title="Blacklisted" />
            </>
          ) : (
            <>
              <KpiMiniCard
                icon="⟳"
                title="Last Campaign Run"
                value={lastRunLabel}
                caption={lastRunWhen ? fmtWhen(lastRunWhen) : ""}
              />
              <KpiMiniCard
                icon="📅"
                title="Scheduled Campaigns"
                value={fmtNumber(scheduledCount)}
                caption={scheduledCount ? "Scheduled broadcasts" : "No scheduled broadcasts"}
              />
              <KpiMiniCard
                icon="💬"
                title="msgs/hr"
                value={fmtNumber(throughput)}
                caption="Throughput for active/scheduled"
              />
              <Box>
                <KpiMiniCard
                  icon="🚫"
                  title="Blacklisted"
                  value={fmtNumber(blacklistStats.total_blacklisted)}
                  caption={`+${blacklistStats.blacklisted_last_7_days} in last 7 days`}
                />
                <Button
                  size="small"
                  color="error"
                  sx={{ mt: 0.75, textTransform: "none", fontWeight: 800 }}
                  onClick={() => setBlacklistOpen(true)}
                >
                  Review Blacklist
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <BlacklistReviewPanel
        open={blacklistOpen}
        onClose={() => setBlacklistOpen(false)}
        onChanged={loadBlacklistStats}
      />

      <Box sx={{ mb: 2 }}>
        <BroadcastBudgetPanel
          title="Email broadcast budget"
          subtitle="Email send quota is enforced before any campaign is queued."
          channels={emailBudget ? [emailBudget] : []}
          loading={emailBudgetLoading}
          onBudgetSaved={loadEmailBudget}
        />
      </Box>

      <Paper sx={{ borderRadius: 3, border: "1px solid #E9ECEF" }} elevation={0}>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary">
            <Tab label="Campaigns" />
            <Tab label="Calendar" />
          </Tabs>
        </Box>
        <Divider />

        <Box sx={{ p: 2 }}>
          {showSkeleton ? (
            <CampaignsListSkeleton />
          ) : tab === 0 ? (
            <CampaignsList
              calendarItems={calendar}
              onSelectCampaign={(c) => { setSelectedCampaign(c); setDetailOpen(true); }}
            />
          ) : (
            <EmailCampaignCalendar
              onSelectCampaign={(c) => { setSelectedCampaign(c); setDetailOpen(true); }}
            />
          )}
        </Box>
      </Paper>

      <CampaignDetailDialog
        campaign={selectedCampaign}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onChanged={() => { load(); setDetailOpen(false); }}
      />
    </Box>
  );
}







